import { useState, FormEvent, useRef, useEffect, useMemo } from 'react'
import { useParams, Link } from 'react-router'
import { v4 as uuidv4 } from 'uuid'
import { useAppDispatch, useAppSelector } from '../../hooks/reduxToolkit'
import { addMessage, updateConversation, updateMessage } from '../../reduxToolkit/conversationsSlice'
import {
  selectConversationById,
  selectMessagesForConversation,
  isAliceConversation,
} from '../../reduxToolkit/conversationsSelectors'
import { NotFound } from '../../screens/notFound'
import { MessageBubble, getMessageChannel } from '../../components/messages/MessageBubble'
import { useSubscriptionPrompt } from '../../hooks/useSubscriptionPrompt'

import './show.scss'

export function AliceConversationShow() {
  const { id } = useParams<{ id: string }>()
  const dispatch = useAppDispatch()
  const { membershipId } = useAppSelector((s) => s.membership)

  const { checkSubscription, SubscriptionPromptModal } = useSubscriptionPrompt({
    trigger: 'always',
    title: 'Subscribe to Chat with Alice',
    message: 'Get unlimited access to Alice, your AI assistant for medical expenses, itemized bills, and provider communication.',
  })

  const conversation = useAppSelector((state) => selectConversationById(state, id || ''))
  const messages = useAppSelector((state) => selectMessagesForConversation(state, id || ''))

  const [newMessage, setNewMessage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Focus textarea on mount
  useEffect(() => {
    textareaRef.current?.focus()
  }, [])

  // Mark unread outbound (Alice) messages as read when the user views the conversation
  useEffect(() => {
    const unreadOutbound = messages.filter(
      (m) => m.direction === 'OUTBOUND' && !m.read_at
    )
    if (unreadOutbound.length === 0) return

    const now = new Date().toISOString()
    for (const msg of unreadOutbound) {
      dispatch(updateMessage({ id: msg.id, updated_at: now, read_at: now }))
    }
  }, [messages, dispatch])

  // Show typing indicator when Alice has read the user's last message but hasn't replied yet
  const showTypingIndicator = useMemo(() => {
    if (messages.length === 0) return false
    const lastMessage = messages[messages.length - 1]
    return lastMessage.direction === 'INBOUND' && !!lastMessage.read_at
  }, [messages])

  // The member's most recent chat message carries the delivery receipt.  Alice
  // marks every unread inbound message read at once when she picks the
  // conversation up, so the latest one alone tells the member how far she's got.
  // Inbound email is the provider writing in, not the member, so it's skipped.
  const lastMemberMessageId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i]
      if (m.direction === 'INBOUND' && getMessageChannel(m) === 'in-app') return m.id
    }
    return undefined
  }, [messages])

  if (!conversation) {
    return <NotFound />
  }

  // Only show reply textarea for Alice conversations
  const showReplyInput = isAliceConversation(conversation)
  const isClosed = conversation.status === 'CLOSED'

  const handleClose = () => {
    const now = new Date().toISOString()
    dispatch(
      updateConversation({
        id: conversation.id,
        updated_at: now,
        status: 'CLOSED',
      })
    )
  }

  const handleReopen = () => {
    const now = new Date().toISOString()
    dispatch(
      updateConversation({
        id: conversation.id,
        updated_at: now,
        status: 'OPEN',
      })
    )
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()

    if (!newMessage.trim() || isSubmitting) return

    // Check subscription before allowing message to Alice
    if (!checkSubscription()) {
      return
    }

    setIsSubmitting(true)

    const now = new Date().toISOString()
    const messageId = uuidv4()

    // Add the new message
    dispatch(
      addMessage({
        id: messageId,
        conversation_id: conversation.id,
        membership_id: membershipId,
        direction: 'INBOUND',
        channel: 'in-app',
        body_text: newMessage.trim(),
        body_html: null,
        subject: null,
        from_address: null,
        to_address: null,
        status: 'DELIVERED',
        sent_at: now,
        read_at: null,
        scheduled_at: null,
        attachments: null,
        created_at: now,
        updated_at: now,
        deleted_at: null,
        error: null,
        external_message_id: null,
        headers: null,
      })
    )

    // Update conversation's last_message_at and reopen if closed
    dispatch(
      updateConversation({
        id: conversation.id,
        updated_at: now,
        last_message_at: now,
        last_message_direction: 'INBOUND',
        ...(isClosed ? { status: 'OPEN' as const } : {}),
      })
    )

    // Clear input before resetting guard so a re-render between
    // the two state updates can't re-submit the same text.
    setNewMessage('')
    setIsSubmitting(false)
    textareaRef.current?.focus()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Submit on Enter without Shift
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e as unknown as FormEvent)
    }
  }

  return (
    <>
      <SubscriptionPromptModal />
      <div className="alice-show">
        <div className="alice-show__header">
          <h1>
            <i className="material-icons-outlined">support_agent</i>
            Alice
            {isClosed && <span className="alice-show__status-badge">Closed</span>}
          </h1>
          {showReplyInput && !isClosed && (
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm alice-show__close-button"
              onClick={handleClose}
            >
              End chat
            </button>
          )}
        </div>

      <div className="alice-show__messages">
        {messages.length === 0 ? (
          <div className="alice-show__empty">
            <p>No messages yet. Start the conversation!</p>
          </div>
        ) : (
          messages.map((message) => (
            <MessageBubble
              key={message.id}
              message={message}
              showDeliveryReceipt={message.id === lastMemberMessageId}
            />
          ))
        )}
        {showTypingIndicator && (
          <div className="alice-message alice-message--alice">
            <div className="alice-message__bubble alice-typing-indicator">
              <span className="alice-typing-indicator__dot" />
              <span className="alice-typing-indicator__dot" />
              <span className="alice-typing-indicator__dot" />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {showReplyInput && isClosed && (
        <div className="alice-show__closed-banner">
          <span>This conversation is closed.</span>
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={handleReopen}
          >
            Reopen
          </button>
        </div>
      )}

      {showReplyInput && !isClosed && (
        <form className="alice-show__reply" onSubmit={handleSubmit}>
          <div className="alice-show__reply-wrapper">
            <textarea
              ref={textareaRef}
              className="form-control"
              placeholder="Type your message..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={2}
              disabled={isSubmitting}
            />
            <button
              type="submit"
              className="btn btn-primary alice-show__send-button"
              disabled={!newMessage.trim() || isSubmitting}
            >
              <i className="material-icons">send</i>
            </button>
          </div>
          <div className="alice-show__reply-hint">
            Press Enter to send, Shift+Enter for new line
          </div>
        </form>
      )}
      </div>
    </>
  )
}
